import { AdminSidebar, DEFAULT_WIDTH, SIDEBAR_WIDTH_KEY } from "@/components/AdminSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { hasAnyRole } from "@/lib/roles";
import { useIsMobile } from "@/hooks/useMobile";
import { ChevronLeft, ChevronRight, Trash2, AlertCircle, Users, Edit, ExternalLink, Bell, Plus } from "lucide-react";
import { useState, useMemo, CSSProperties } from "react";
import { toast } from "sonner";
import { format, addWeeks, subWeeks, startOfWeek, endOfWeek, addDays } from "date-fns";
import { de } from "date-fns/locale";
import { useLocation } from "wouter";

const WEEKDAYS = [1, 2, 3, 4, 5]; // Monday to Friday
const TIME_SLOTS = Array.from({ length: 17 }, (_, i) => i + 6); // 6:00 to 22:00

export default function GymSlotsCalendar() {
  const { loading, user } = useAuth();
  const { data: config } = trpc.config.getTenantConfig.useQuery();
  const isMobile = useIsMobile();
  const [, setLocation] = useLocation();
  
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });

  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState<any | null>(null);
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [maxParticipants, setMaxParticipants] = useState<number>(5);
  
  // Generate slots form state
  const [generateRange, setGenerateRange] = useState({
    startDate: format(new Date(), "yyyy-MM-dd"),
    endDate: format(addDays(new Date(), 7), "yyyy-MM-dd")
  });

  const utils = trpc.useUtils();
  
  const weekStart = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(currentWeek, { weekStartsOn: 1 });

  const { data: slots, isLoading } = trpc.gym.slots.list.useQuery({
    startDate: weekStart.toISOString(),
    endDate: weekEnd.toISOString(),
    includeInactive: true
  });

  const generateSlotsMutation = trpc.gym.templates.generateSlots.useMutation({
    onSuccess: (result) => {
      toast.success(`${result.created} Slots aus Vorlagen generiert`);
      utils.gym.slots.list.invalidate();
      setIsGenerateDialogOpen(false);
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const deleteSlotMutation = trpc.gym.slots.delete.useMutation({
    onSuccess: () => {
      toast.success("Slot gelöscht");
      utils.gym.slots.list.invalidate();
      setIsManageDialogOpen(false);
      setSelectedSlot(null);
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const cancelSlotMutation = trpc.gym.slots.cancelWithNotification.useMutation({
    onSuccess: () => {
      toast.success("Slot storniert und Mitglieder benachrichtigt");
      utils.gym.slots.list.invalidate();
      setIsManageDialogOpen(false);
      setSelectedSlot(null);
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const updateMaxParticipantsMutation = trpc.gym.slots.updateMaxParticipants.useMutation({
    onSuccess: () => {
      toast.success("Maximale Teilnehmer aktualisiert");
      utils.gym.slots.list.invalidate();
      setIsManageDialogOpen(false);
      setSelectedSlot(null);
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  // Group slots by day
  const slotsByDay = useMemo(() => {
    if (!slots) return {};
    const grouped: Record<number, typeof slots> = {};
    
    slots.forEach(slot => {
      const date = new Date(slot.startTime);
      const day = date.getDay() === 0 ? 7 : date.getDay(); // Convert Sunday from 0 to 7
      if (WEEKDAYS.includes(day)) {
        if (!grouped[day]) grouped[day] = [];
        grouped[day].push(slot);
      }
    });
    
    return grouped;
  }, [slots]);

  const handleSlotClick = (slot: any) => {
    setSelectedSlot(slot);
    setMaxParticipants(slot.maxParticipants);
    setIsManageDialogOpen(true);
  };

  const handleDeleteSlot = () => {
    if (!selectedSlot) return;
    if (selectedSlot.bookingCount > 0) {
      toast.error("Slot kann nicht gelöscht werden, da Buchungen vorhanden sind");
      return;
    }
    deleteSlotMutation.mutate({ slotId: selectedSlot.id });
  };

  const handleCancelSlot = () => {
    if (!selectedSlot) return;
    cancelSlotMutation.mutate({ slotId: selectedSlot.id });
  };

  const handleUpdateMaxParticipants = () => {
    if (!selectedSlot) return;
    if (maxParticipants < selectedSlot.bookingCount) {
      toast.error(`Maximale Teilnehmer kann nicht kleiner als ${selectedSlot.bookingCount} sein (aktuelle Buchungen)`);
      return;
    }
    updateMaxParticipantsMutation.mutate({
      slotId: selectedSlot.id,
      maxParticipants
    });
  };

  const handleViewBookings = () => {
    if (!selectedSlot) return;
    setLocation(`/admin/gym/bookings?slotId=${selectedSlot.id}`);
  };

  if (loading) {
    return null;
  }

  if (!user) {
    window.location.href = getLoginUrl();
    return null;
  }

  if (!hasAnyRole(user.roles, ["admin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Zugriff verweigert</p>
      </div>
    );
  }

  const weekDays = WEEKDAYS.map(day => {
    const date = new Date(weekStart);
    date.setDate(weekStart.getDate() + (day - 1));
    return { day, date };
  });

  return (
    <SidebarProvider>
      <AdminSidebar setSidebarWidth={setSidebarWidth} />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Slot-Kalender</h1>
            <p className="text-sm text-muted-foreground">Verwalten Sie Gym-Slots</p>
          </div>
        </header>

        <div className="flex-1 p-6 overflow-auto">
          <Card>
            <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <CardTitle>Wochenansicht</CardTitle>
                  <CardDescription>
                    {format(weekStart, "d. MMMM", { locale: de })} - {format(weekEnd, "d. MMMM yyyy", { locale: de })}
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => setIsGenerateDialogOpen(true)}
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Slots generieren
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCurrentWeek(subWeeks(currentWeek, 1))}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setCurrentWeek(new Date())}
                    >
                      Heute
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="text-center py-8">Lädt...</div>
              ) : (
                <div className="overflow-x-auto">
                  {/* Desktop Calendar View */}
                  <div className="hidden md:block min-w-[800px]">
                    <div className="grid grid-cols-6 border rounded-lg overflow-hidden">
                      {/* Header */}
                      <div className="bg-muted p-3 border-r border-b font-semibold">Zeit</div>
                      {weekDays.map(({ day, date }) => (
                        <div key={day} className="bg-muted p-3 border-r last:border-r-0 border-b text-center">
                          <div className="font-semibold">
                            {format(date, "EEEE", { locale: de })}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {format(date, "d.MM.", { locale: de })}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {slotsByDay[day]?.length || 0} Slots
                          </div>
                        </div>
                      ))}

                      {/* Time grid */}
                      <div className="col-span-6 relative" style={{ height: `${TIME_SLOTS.length * 60}px` }}>
                        {/* Time labels and grid lines */}
                        {TIME_SLOTS.map((hour, idx) => (
                          <div key={hour} className="absolute w-full grid grid-cols-6" style={{ top: `${idx * 60}px`, height: '60px' }}>
                            <div className="border-r border-b p-2 text-sm text-muted-foreground">
                              {hour.toString().padStart(2, '0')}:00
                            </div>
                            {WEEKDAYS.map(day => (
                              <div key={day} className="border-r last:border-r-0 border-b relative" />
                            ))}
                          </div>
                        ))}

                        {/* Slot blocks overlay */}
                        <div className="absolute inset-0 pointer-events-none">
                          <div className="grid grid-cols-6 h-full">
                            <div /> {/* Time column spacer */}
                            {WEEKDAYS.map(day => {
                              const daySlots = slotsByDay[day] || [];
                              
                              return (
                                <div key={day} className="relative">
                                  {daySlots.map(slot => {
                                    const startDate = new Date(slot.startTime);
                                    const endDate = new Date(slot.endTime);
                                    const startHour = startDate.getHours();
                                    const startMin = startDate.getMinutes();
                                    const endHour = endDate.getHours();
                                    const endMin = endDate.getMinutes();
                                    
                                    const top = ((startHour - 6) * 60 + startMin);
                                    const height = ((endHour - startHour) * 60 + (endMin - startMin));
                                    
                                    const bookingCount = slot.bookingCount || 0;
                                    const maxParticipants = slot.maxParticipants;
                                    const fillPercentage = maxParticipants > 0 ? (bookingCount / maxParticipants) * 100 : 0;
                                    
                                    // Color coding based on booking percentage
                                    // Green: < 50% booked, Yellow: 50-80% booked, Orange: 80-99% booked, Red: 100% full
                                    let bgColor = 'bg-green-50';
                                    let textColor = 'text-green-900';
                                    let badgeBg = 'bg-green-100';
                                    let badgeText = 'text-green-700';
                                    
                                    if (fillPercentage >= 100) {
                                      bgColor = 'bg-red-50';
                                      textColor = 'text-red-900';
                                      badgeBg = 'bg-red-100';
                                      badgeText = 'text-red-700';
                                    } else if (fillPercentage >= 80) {
                                      bgColor = 'bg-orange-50';
                                      textColor = 'text-orange-900';
                                      badgeBg = 'bg-orange-100';
                                      badgeText = 'text-orange-700';
                                    } else if (fillPercentage >= 50) {
                                      bgColor = 'bg-yellow-50';
                                      textColor = 'text-yellow-900';
                                      badgeBg = 'bg-yellow-100';
                                      badgeText = 'text-yellow-700';
                                    }
                                    
                                    return (
                                      <button
                                        key={slot.id}
                                        onClick={() => handleSlotClick(slot)}
                                        className={`absolute rounded border-l-4 p-2 pointer-events-auto transition-all hover:shadow-md hover:z-10 cursor-pointer ${bgColor} ${textColor}`}
                                        style={{
                                          top: `${top}px`,
                                          height: `${Math.max(height - 2, 40)}px`,
                                          left: '2px',
                                          width: 'calc(100% - 4px)',
                                          borderLeftColor: slot.service?.color ?? '#06b6d4'
                                        }}
                                      >
                                        <p className="text-xs font-medium truncate">{slot.service?.name}</p>
                                        <p className="text-xs opacity-75">
                                          {format(startDate, "HH:mm")} - {format(endDate, "HH:mm")}
                                        </p>
                                        <div className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${badgeBg} ${badgeText}`}>
                                          <Users className="h-3 w-3" />
                                          {bookingCount}/{maxParticipants}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile List View */}
                  <div className="md:hidden space-y-4">
                    {weekDays.map(({ day, date }) => {
                      const daySlots = slotsByDay[day] || [];
                      return (
                        <div key={day}>
                          <h3 className="font-semibold mb-2">
                            {format(date, "EEEE, d. MMMM", { locale: de })}
                          </h3>
                          {daySlots.length === 0 ? (
                            <p className="text-sm text-muted-foreground">Keine Slots</p>
                          ) : (
                            <div className="space-y-2">
                              {daySlots.map(slot => (
                                <Card
                                  key={slot.id}
                                  className="cursor-pointer hover:bg-accent/50"
                                  onClick={() => handleSlotClick(slot)}
                                >
                                  <CardContent className="p-4">
                                    <div className="flex items-center gap-3">
                                      <div
                                        className="w-1 h-12 rounded"
                                        style={{ backgroundColor: slot.service?.color ?? '#06b6d4' }}
                                      />
                                      <div className="flex-1">
                                        <p className="font-medium">{slot.service?.name}</p>
                                        <p className="text-sm text-muted-foreground">
                                          {format(new Date(slot.startTime), "HH:mm")} - {format(new Date(slot.endTime), "HH:mm")}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                          {slot.bookingCount || 0}/{slot.maxParticipants} Plätze
                                        </p>
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </SidebarInset>

      {/* Slot Management Dialog */}
      <Dialog open={isManageDialogOpen} onOpenChange={setIsManageDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Slot verwalten</DialogTitle>
            <DialogDescription>
              Aktionen für diesen Trainingsslot
            </DialogDescription>
          </DialogHeader>

          {selectedSlot && (
            <div className="space-y-4">
              {/* Slot Info */}
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <div className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: selectedSlot.service?.color ?? '#06b6d4' }}
                  />
                  <p className="font-semibold">{selectedSlot.service?.name}</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(selectedSlot.startTime), "EEEE, d. MMMM yyyy", { locale: de })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(selectedSlot.startTime), "HH:mm")} - {format(new Date(selectedSlot.endTime), "HH:mm")}
                </p>
                <div className="flex items-center gap-2 text-sm">
                  <Users className="h-4 w-4" />
                  <span>{selectedSlot.bookingCount || 0} / {selectedSlot.maxParticipants} Buchungen</span>
                </div>
              </div>

              {/* Edit Max Participants */}
              <div className="space-y-2">
                <Label htmlFor="maxParticipants">Maximale Teilnehmer</Label>
                <div className="flex gap-2">
                  <Input
                    id="maxParticipants"
                    type="number"
                    min={selectedSlot.bookingCount || 0}
                    value={maxParticipants}
                    onChange={(e) => setMaxParticipants(parseInt(e.target.value))}
                  />
                  <Button
                    onClick={handleUpdateMaxParticipants}
                    disabled={maxParticipants === selectedSlot.maxParticipants || updateMaxParticipantsMutation.isPending}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Speichern
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={handleViewBookings}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Buchungen anzeigen ({selectedSlot.bookingCount || 0})
                </Button>

                {selectedSlot.bookingCount > 0 ? (
                  <Button
                    variant="destructive"
                    className="w-full justify-start"
                    onClick={handleCancelSlot}
                    disabled={cancelSlotMutation.isPending}
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Slot stornieren & Mitglieder benachrichtigen
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    className="w-full justify-start"
                    onClick={handleDeleteSlot}
                    disabled={deleteSlotMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Slot löschen
                  </Button>
                )}
              </div>

              {selectedSlot.bookingCount > 0 && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Dieser Slot hat {selectedSlot.bookingCount} Buchung(en). Bei Stornierung werden alle gebuchten Mitglieder per E-Mail benachrichtigt.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManageDialogOpen(false)}>
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate Slots Dialog */}
      <Dialog open={isGenerateDialogOpen} onOpenChange={setIsGenerateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Slots aus Vorlagen generieren</DialogTitle>
            <DialogDescription>
              Generieren Sie automatisch Slots basierend auf Ihren definierten Slot-Vorlagen für einen bestimmten Zeitraum
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="startDate">Von</Label>
              <Input
                id="startDate"
                type="date"
                value={generateRange.startDate}
                onChange={(e) => setGenerateRange({ ...generateRange, startDate: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">Bis</Label>
              <Input
                id="endDate"
                type="date"
                value={generateRange.endDate}
                onChange={(e) => setGenerateRange({ ...generateRange, endDate: e.target.value })}
              />
            </div>
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                Es werden Slots für alle aktiven Slot-Vorlagen im angegebenen Zeitraum erstellt.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsGenerateDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => {
                generateSlotsMutation.mutate({
                  startDate: generateRange.startDate,
                  endDate: generateRange.endDate
                });
              }}
              disabled={generateSlotsMutation.isPending}
            >
              {generateSlotsMutation.isPending ? "Generiere..." : "Generieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarProvider>
  );
}
